import { useId, useState } from 'react';
import {
  ALL_KINDS, EMPTY_VOCABULARY, labelFor, pickerOptions, stageNote, toggle, withdrawnIds, withdrawnNote,
  type CrmTagKind, type CrmTagVocabulary,
} from './crmTagSelection.js';

/**
 * CRM tags, chosen one way everywhere: selected tags as chips, and "+ Add tag".
 *
 * Email capture labels and every Review detail use this one control over the one vocabulary (GET /crm-tags). Which internal field a tag
 * travels in is crmTagSelection.ts's job; nothing here says "stage" or "relationship".
 */

const WRAP = '[overflow-wrap:anywhere]';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';

export { EMPTY_VOCABULARY };

/**
 * One chosen tag. Long names wrap; the full value stays in the title.
 *
 * A WITHDRAWN tag — chosen before it was removed or archived, or whose CRM tag is gone —
 * keeps its name and says it is no longer available. It is never silently dropped or
 * swapped for another: the person removes it, and until then the save is blocked.
 */
function Chip({ label, hint, withdrawn, onRemove, disabled }: {
  label: string; hint?: string; withdrawn: boolean; onRemove?: () => void; disabled?: boolean;
}) {
  const tone = withdrawn
    ? 'border-amber-300 bg-amber-50 text-amber-950'
    : onRemove ? 'border-slate-300 bg-white text-slate-900' : 'border-slate-200 bg-slate-50 text-slate-900';
  return (
    <li
      className={`inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border text-sm ${tone} ${
        onRemove ? 'min-h-9 py-0.5 pl-3 pr-0.5' : 'min-h-7 px-2.5 py-0.5'
      }`}
      data-crm-tag-chip=""
      data-withdrawn={withdrawn ? '' : undefined}
    >
      <span className={`min-w-0 ${WRAP}`} title={withdrawn ? `${label} · no longer available` : label}>
        {label}
        {withdrawn ? <span className="text-xs text-amber-900"> · no longer available</span> : null}
        {hint && !withdrawn ? <span className="ml-1.5 text-xs text-slate-500">{hint}</span> : null}
      </span>
      {onRemove ? (
        <button
          type="button" onClick={onRemove} disabled={disabled}
          aria-label={`Remove ${label}`}
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base leading-none text-slate-500 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-50 ${FOCUS}`}
        >
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
    </li>
  );
}

/** Chosen tags, read-only: a label mapping's summary, a row's tags. */
export function CrmTagChips({ ids, vocab, empty = 'None' }: {
  ids: readonly string[]; vocab: CrmTagVocabulary; empty?: string;
}) {
  if (ids.length === 0) return <p className="text-sm text-slate-500">{empty}</p>;
  const gone = withdrawnIds(ids, vocab);
  return (
    <ul className="flex min-w-0 flex-wrap gap-1.5" aria-label="CRM tags">
      {ids.map((id) => (
        <Chip key={id} label={labelFor(id, vocab)} withdrawn={gone.includes(id)} />
      ))}
    </ul>
  );
}

export function CrmTagPicker({
  vocab, selected, onChange, kinds = ALL_KINDS, disabled, legend = 'CRM Tags', hints,
  empty = 'No CRM tags chosen.', onManageTags, legendClassName = 'text-sm font-medium text-slate-800',
}: {
  vocab: CrmTagVocabulary;
  selected: readonly string[];
  onChange: (ids: string[]) => void;
  /** Image and file capture leave relationship types out. */
  kinds?: readonly CrmTagKind[];
  disabled?: boolean;
  legend?: string;
  legendClassName?: string;
  /** A short note beside a chip: "from label", "from your CRM". */
  hints?: Readonly<Record<string, string>>;
  empty?: string;
  /** Where to add tags when there are none to choose from. */
  onManageTags?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const searchId = useId();
  const listId = useId();

  const options = pickerOptions(vocab, kinds);
  const available = options.filter((t) => !selected.includes(t.id));
  const needle = filter.trim().toLowerCase();
  const shown = needle ? available.filter((t) => t.label.toLowerCase().includes(needle)) : available;
  const note = stageNote(vocab, kinds);

  const pick = (id: string) => onChange(toggle(selected, id, vocab));
  const gone = withdrawnIds(selected, vocab);
  const blocked = withdrawnNote(selected, vocab);

  return (
    <fieldset className="min-w-0" data-crm-tag-picker="">
      <legend className={legendClassName}>{legend}</legend>
      {selected.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="mt-2 flex min-w-0 flex-wrap gap-1.5" aria-label="Chosen CRM tags">
          {selected.map((id) => {
            const label = labelFor(id, vocab);
            return (
              <Chip
                key={id} label={label} hint={hints?.[id]} withdrawn={gone.includes(id)}
                disabled={disabled} onRemove={() => onChange(selected.filter((x) => x !== id))}
              />
            );
          })}
        </ul>
      )}
      {blocked ? (
        <p className={`mt-2 text-sm text-amber-900 ${WRAP}`} data-withdrawn-note="">{blocked}</p>
      ) : null}

      {!open ? (
        <button
          type="button" disabled={disabled}
          aria-expanded={false} aria-controls={listId}
          onClick={() => setOpen(true)}
          className={`mt-2 inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`}
        >
          + Add tag
        </button>
      ) : (
        <div id={listId} className="mt-2 min-w-0 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-crm-tag-options="">
          {options.length === 0 ? (
            <p className="text-sm text-slate-600">
              No CRM tags are available yet.{' '}
              {onManageTags ? (
                <button type="button" onClick={onManageTags} className={`inline-flex min-h-11 items-center font-medium text-slate-900 underline ${FOCUS}`}>
                  Choose them in CRM Tags
                </button>
              ) : 'Choose them in Settings → CRM Tags.'}
            </p>
          ) : (
            <>
              <label htmlFor={searchId} className="block text-sm text-slate-700">Search CRM tags</label>
              <input
                id={searchId} type="search" value={filter} disabled={disabled}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Filter ${available.length} tag${available.length === 1 ? '' : 's'}`}
                className={`min-h-11 w-full min-w-0 max-w-md rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm ${FOCUS}`}
              />
              {note ? <p className="text-xs text-slate-500">{note}</p> : null}
              {available.length === 0 ? (
                <p className="text-sm text-slate-600">Every available tag is already on this contact.</p>
              ) : shown.length === 0 ? (
                <p className={`text-sm text-slate-600 ${WRAP}`}>No tag matches “{filter}”.</p>
              ) : (
                <ul className="max-h-64 min-w-0 overflow-y-auto rounded-md border border-slate-200 bg-white" aria-label="CRM tags you can add">
                  {shown.map((t) => (
                    <li key={t.id} className="border-b border-slate-100 last:border-b-0">
                      <button
                        type="button" disabled={disabled} title={t.label}
                        onClick={() => pick(t.id)}
                        className={`flex min-h-11 w-full min-w-0 items-center px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50 ${WRAP} ${FOCUS}`}
                      >
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <button
            type="button" aria-expanded={true}
            onClick={() => { setOpen(false); setFilter(''); }}
            className={`inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 ${FOCUS}`}
          >
            Done
          </button>
        </div>
      )}
    </fieldset>
  );
}
